import { useState } from 'react';
import { Button, Container, Group, Paper, SimpleGrid, Skeleton, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconDeviceFloppy, IconMail } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { useMe } from '@/hooks/use-me';
import { getErrorMessage } from '@/lib/error-message';
import { graphql } from '@/lib/graphql';

const MyProfileSubscription = graphql(`
  subscription MyProfile($userId: Uuid!) {
    usersByPk(id: $userId) {
      id
      email
      firstName
      lastName
    }
  }
`);

const UpdateMyProfileMutation = graphql(`
  mutation UpdateMyProfile($userId: Uuid!, $firstName: String, $lastName: String) {
    updateUsersByPk(pkColumns: { id: $userId }, _set: { firstName: $firstName, lastName: $lastName }) {
      id
      firstName
      lastName
    }
  }
`);

export const Route = createFileRoute('/_authed/user')({
  component: UserProfilePage,
});

function LoadingSkeleton() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Skeleton height={50} width={250} radius="xl" />
        <Skeleton height={200} radius="xl" />
      </Stack>
    </Container>
  );
}

type ProfileDraft = { firstName: string; lastName: string };

function UserProfilePage() {
  const { me } = useMe();
  const [{ data }] = useSubscription({ query: MyProfileSubscription, variables: { userId: me.id } });
  const [saveState, saveProfile] = useMutation(UpdateMyProfileMutation);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (data === undefined) return <LoadingSkeleton />;

  const profile = data.usersByPk;
  const firstName = draft?.firstName ?? profile?.firstName ?? '';
  const lastName = draft?.lastName ?? profile?.lastName ?? '';

  const submit = async () => {
    setError(null);

    const result = await saveProfile({
      userId: me.id,
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
    });

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to save your profile'));

    setDraft(null);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>User Settings</Title>
          <Text c="dimmed" mt="xs" size="lg">
            Manage your account
          </Text>
        </div>

        <Paper p="xl" radius="lg" withBorder>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Stack gap="lg">
              <Title order={3}>Profile</Title>

              <ErrorAlert message={error} />

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="First Name"
                  value={firstName}
                  onChange={(event) => setDraft({ firstName: event.currentTarget.value, lastName })}
                />
                <TextInput
                  label="Last Name"
                  value={lastName}
                  onChange={(event) => setDraft({ firstName, lastName: event.currentTarget.value })}
                />
              </SimpleGrid>

              <Group gap="md">
                <IconMail size={18} style={{ color: 'var(--mantine-color-dimmed)' }} />
                <div>
                  <Text size="xs" c="dimmed" fw={500}>
                    Email
                  </Text>
                  <Text size="sm" fw={500}>
                    {profile?.email ?? me.email}
                  </Text>
                </div>
              </Group>

              <Group justify="flex-end">
                <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveState.fetching}>
                  Save Changes
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Container>
  );
}
